import { parseIsraelLocalDateTime, validateJobInput, type JobField, type JobWriteRequest } from '@crew/shared';
import { useId, useState, type FormEvent } from 'react';
import { ApiClientError } from '@/services/apiError';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Notice';
import { TextField } from '../ui/TextField';

export interface JobFormValues {
  name: string;
  date: string;
  reportTime: string;
  location: string;
  notes: string;
  /** "YYYY-MM-DDTHH:mm" בשעון ישראל, או ריק */
  closesAtLocal: string;
}

export function toRequest(v: JobFormValues): JobWriteRequest {
  const closes = v.closesAtLocal ? parseIsraelLocalDateTime(v.closesAtLocal) : null;
  return {
    name: v.name,
    date: v.date,
    reportTime: v.reportTime,
    location: v.location,
    notes: v.notes,
    registrationClosesAt: closes ? closes.toISOString() : null,
  };
}

/**
 * טופס יצירה ועריכה של עבודה. הבדיקות זהות לאלה שבשרת (validateJobInput המשותף),
 * כך שהשגיאות מופיעות מיד ליד השדה, והשרת בודק שוב.
 */
export function JobForm({
  initial,
  isNew,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: JobFormValues;
  isNew: boolean;
  submitLabel: string;
  onSubmit: (values: JobFormValues) => Promise<void>;
  onCancel?: () => void;
}) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<Partial<Record<JobField, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [autoClose, setAutoClose] = useState(Boolean(initial.closesAtLocal));
  const notesId = useId();
  const closeId = useId();

  const set = (k: keyof JobFormValues) => (e: { target: { value: string } }) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k === 'closesAtLocal' ? 'registrationClosesAt' : k]: undefined }));
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const final = autoClose ? values : { ...values, closesAtLocal: '' };
    if (autoClose && !final.closesAtLocal) {
      setErrors({ registrationClosesAt: 'יש לבחור מועד, או לבטל את הסגירה האוטומטית' });
      return;
    }
    const check = validateJobInput(toRequest(final), new Date(), { isNew });
    if (!check.ok) {
      setErrors(check.errors);
      return;
    }
    setBusy(true);
    try {
      await onSubmit(final);
    } catch (err) {
      if (err instanceof ApiClientError && err.fields) setErrors(err.fields as Partial<Record<JobField, string>>);
      setFormError(err instanceof Error ? err.message : 'השמירה נכשלה');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      {formError && <Notice tone="error">{formError}</Notice>}

      <TextField label="שם העבודה" value={values.name} onChange={set('name')} error={errors.name} placeholder="לדוגמה: אבטחת אירוע — היכל התרבות" maxLength={120} required />

      <div className="grid grid-cols-2 gap-3">
        <TextField label="תאריך" type="date" value={values.date} onChange={set('date')} error={errors.date} required />
        <TextField label="שעת התייצבות" type="time" value={values.reportTime} onChange={set('reportTime')} error={errors.reportTime} required />
      </div>

      <TextField label="מיקום" value={values.location} onChange={set('location')} error={errors.location} placeholder="כתובת או שם המקום" maxLength={200} required />

      <div>
        <label htmlFor={notesId} className="mb-1.5 block text-[14px] font-medium text-ink-2">
          הערות והנחיות <span className="font-normal text-ink-3">(לא חובה)</span>
        </label>
        <textarea
          id={notesId}
          value={values.notes}
          onChange={set('notes')}
          rows={3}
          maxLength={2000}
          placeholder="ציוד, לבוש, נקודת מפגש, איש קשר באתר"
          className={[
            'block w-full rounded-control border bg-surface px-3.5 py-3 text-[16px] text-ink placeholder:text-ink-3',
            'focus:border-brand focus:outline-none focus:ring-3 focus:ring-brand/20',
            errors.notes ? 'border-danger' : 'border-line-strong',
          ].join(' ')}
        />
        {errors.notes && <p className="mt-1.5 text-[13px] text-danger">{errors.notes}</p>}
      </div>

      <fieldset className="rounded-control border border-line bg-paper/60 p-4">
        <label htmlFor={closeId} className="flex cursor-pointer items-start gap-3">
          <input
            id={closeId}
            type="checkbox"
            checked={autoClose}
            onChange={(e) => setAutoClose(e.target.checked)}
            className="mt-1 size-5 shrink-0 accent-[var(--color-brand)]"
          />
          <span>
            <span className="block text-[15px] font-semibold">סגירת הרשמה אוטומטית</span>
            <span className="block text-[13px] text-ink-2">ההרשמה תיסגר לבד במועד שתבחרו. אפשר תמיד לסגור או לפתוח ידנית.</span>
          </span>
        </label>
        {autoClose && (
          <TextField
            className="mt-4"
            label="מועד סגירת ההרשמה"
            type="datetime-local"
            value={values.closesAtLocal}
            onChange={set('closesAtLocal')}
            error={errors.registrationClosesAt}
          />
        )}
      </fieldset>

      <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row">
        {onCancel && (
          <Button variant="secondary" onClick={onCancel} disabled={busy} className="sm:min-w-32">
            ביטול
          </Button>
        )}
        <Button type="submit" loading={busy} className="flex-1 sm:flex-none sm:min-w-44">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
